/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import React from 'react';

import { cssMap, jsx } from '@compiled/react';
import { di } from 'react-magnetic-di';

import { fg } from '@atlaskit/platform-feature-flags';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import {
	setHubBrandingStyle,
	useHubContext,
	useNavigationStyle,
	useUnsetGlobalTheme,
} from '@atlassian/company-hub-common';

import { Banner } from './banner';
import { Navbar } from './navbar';

const containerStyles = cssMap({
	bannerContainer: {
		paddingLeft: token('space.300'),
		paddingRight: token('space.300'),
		paddingBottom: token('space.300'),
	},
	container: {
		boxSizing: 'border-box',
		appearance: 'none',
		border: 'none',
		position: 'relative',
		backgroundColor: token('elevation.surface.raised'),
		overflow: 'auto',
		minHeight: '100%',
		display: 'flex',
		flexDirection: 'column',
		// #ref - help-center/packages/common-utils/styles/src/global.tsx
		// we can't use imported tokens here so hard coding the value instead but this is from the @atlassian/company-hub-common package
		/* eslint-disable @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors */
		'& a:not(.cv-exclude-from-customisation):not([data-exclude-global-styling="true"])': {
			color: 'var(--hub-primary-link-color)',
		},
		'& a:hover:not(.cv-exclude-from-customisation):not([data-exclude-global-styling="true"])': {
			color: 'var(--hub-primary-link-color)',
		},
	},
	childrenWrapper: {
		paddingBottom: token('space.600'),
	},
	childrenWrapperEditMode: {
		// In edit mode, the wrapper must fill the remaining flex space so
		// that the editor's `height: 100%` chain resolves correctly.
		// Without this, the config panel loses its fixed height and scrolls
		// together with the page content instead of independently.
		flexGrow: 1,
		minHeight: '0px',
	},
});

export const HubAppShell = ({
	children,
	isEditMode = false,
}: {
	children: React.ReactNode;
	isEditMode?: boolean;
}): JSX.Element => {
	di(useHubContext, fg);
	const { layout, branding, containerStyling } = useHubContext();
	const { options: navStyle } = useNavigationStyle();

	const fixedNavBarStyling = navStyle?.fixedAtTop ? { height: '100%' } : {};

	const showBanner = layout?.showBanner;
	const primaryColor = branding?.colors?.primary;
	const shouldApplyContainerStyling = fg('enable-jsm-in-hubs');

	// Default hardcoded styles (used when feature flag is disabled for backward compatibility)
	const defaultContainerStyling = {
		borderRadius: token('radius.large'),
		boxShadow: token('elevation.shadow.overlay'),
	};

	// Apply containerStyling from props if feature flag is enabled, otherwise use hardcoded defaults
	const appliedContainerStyling = shouldApplyContainerStyling
		? containerStyling
		: defaultContainerStyling;

	React.useEffect(() => {
		// we are targing whole document for branding style, because we need these variables in react portals as well, e.g. color picker
		return setHubBrandingStyle({ primaryColor });
	}, [primaryColor]);

	const hubContainerRef = React.useRef<HTMLDivElement>(null);
	useUnsetGlobalTheme(isEditMode ? hubContainerRef.current : null); // for view mode we don't need to unset the global theme

	return (
		<Box
			xcss={containerStyles.container}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Dynamic styles passed from outside via containerStyling prop
			style={{ ...appliedContainerStyling, ...fixedNavBarStyling }}
			ref={hubContainerRef}
		>
			<Navbar isEditMode={isEditMode} />
			{showBanner && (
				<div css={containerStyles.bannerContainer}>
					<Banner />
				</div>
			)}
			<Box
				xcss={isEditMode ? containerStyles.childrenWrapperEditMode : containerStyles.childrenWrapper}
				testId="hub-app-shell-children-wrapper"
			>
				{children}
			</Box>
		</Box>
	);
};
